/**
 * useSpamProtection — client-side anti-spam layer for any form on the site.
 *
 * What it does:
 *  1. Honeypot field — spread `honeypotProps` on a hidden <input>. Real users
 *     never see it; bots love to fill `url`/`website` fields.
 *  2. Time-trap — submissions in under MIN_FILL_MS from mount are flagged.
 *  3. Keyword blocklist — SPAM_PATTERNS is matched against any free-text
 *     fields you pass into checkSpam().
 *
 * Usage:
 *   const { honeypotProps, checkSpam } = useSpamProtection();
 *   ...
 *   const { isSpam } = checkSpam({ name, email, message });
 *   if (isSpam) { setSubmitted(true); return; } // silent success
 *
 * To add a new pattern: append a new RegExp to SPAM_PATTERNS below. Keep
 * patterns case-insensitive (/i) and prefer \b word-boundaries to avoid
 * false positives on legitimate messages.
 */
import { useMemo, useRef, useState } from "react";
import { learnFromSubmission, textHasLearnedToken } from "./spamLearnedAllowlist";
import { compileExtras, readOverrides } from "./spamWhitelistOverrides";
import { recordSpamEvent } from "./spamDebugLog";
import { normalizeAddressText } from "./normalizeAddress";
import { detectVariantLabel } from "./spamVariantDetect";

const MIN_FILL_MS = 3000;

export const SPAM_PATTERNS: RegExp[] = [
  // SEO / agency pitches
  /\b(seo services|seo expert|seo agency|rank(ing)? (your|on google|#1)|first page of google)\b/i,
  /\b(backlinks?|link building|guest post|sponsored post|niche edit|dofollow)\b/i,
  /\b(we (can|will) (build|design|rank|improve)|our agency offers|we offer premium)\b/i,
  /\b(web design services|website redesign|we noticed your (website|site))\b/i,
  /\bincrease (your )?(traffic|leads|sales|revenue) by \d+%/i,

  // Generic cold outreach tells
  /\b(dear (sir|madam|owner|ceo|admin|webmaster))\b/i,
  /\b(i hope this (email|message) finds you well)\b/i,
  /\b(saw your website and|came across your site)\b/i,

  // Scams / off-topic
  /\b(crypto|bitcoin|forex|binary options|investment opportunity|trading signals)\b/i,
  /\b(loan (offer|approved)|mortgage rate|debt consolidation)\b/i,
  /\b(adult|escort|viagra|cialis|pharmacy)\b/i,

  // Structural tells
  /https?:\/\/[^\s]+.*https?:\/\/[^\s]+/i, // two or more URLs
  /\b[a-z0-9]{20,}\b/i, // long random strings
];

/**
 * WHITELIST_PATTERNS — when ANY of these match the message, the keyword
 * blocklist is skipped entirely. Honeypot and time-trap still apply.
 *
 * Use this for legitimate electrical/HVAC/energy terminology that might
 * otherwise overlap with a spam pattern (e.g. "loan" for equipment loans,
 * "rate" for utility rates). Keep entries case-insensitive (/i) and
 * specific enough that bots wouldn't naturally include them.
 *
 * To add a new whitelist phrase: append a new RegExp below.
 */
export const WHITELIST_PATTERNS: RegExp[] = [
  // Core trade vocabulary — strong signal of a real customer inquiry
  /\b(electric(al|ian)?|hvac|heating|cooling|air ?condition(ing|er)?|a\/?c\b)/i,
  /\b(furnace|heat pump|boiler|thermostat|ductwork|ducts?)\b/i,
  /\b(panel|breaker|outlet|wiring|rewire|circuit|generator|transformer|sub ?panel)\b/i,
  /\b(lighting|led|retrofit|fixture|ballast|stadium light|sports light)\b/i,
  /\b(energy (audit|management|bill|usage|efficiency)|kwh|utility (bill|rate)s?|demand charge)\b/i,
  /\b(solar|ev charger|building automation|controls?|bms)\b/i,

  // Service intent
  /\b(estimate|quote|bid|proposal|repair|install(ation)?|service call|maintenance|inspection)\b/i,
  /\b(emergency|outage|no power|breaker tripp(ed|ing)|smell(s|ing)? (gas|burning))\b/i,

  // Local geography — Hadley's KY + Southern Indiana service territory.
  // Kept strict: no bare two-letter codes like "in" (false-positive on the
  // English word "in"); state-code KY only matches when adjacent to a comma,
  // a city name, or end of string.
  //
  // Cities below are sourced from src/data/locations.ts. When you add a new
  // location there, also add it here so its inquiries get whitelisted.
  /\b(louisville|lexington|frankfort|bowling green|owensboro|georgetown|richmond|nicholasville|danville|elizabethtown|bardstown|shelbyville|simpsonville|la ?grange|jeffersontown|st\.? matthews|mt\.? washington|shepherdsville|hillview|radcliff|fort knox|cox'?s creek|covington|newport|florence|burlington|erlanger|independence)\b/i,
  /\b(new albany|jeffersonville|clarksville|sellersburg|charlestown|madison|salem)\b/i,
  // Counties Hadley serves
  /\b(jefferson|oldham|bullitt|shelby|spencer|henry|trimble|nelson|hardin|meade|breckinridge|grayson|larue|marion|washington|anderson|franklin|woodford|fayette|scott|boone|kenton|campbell|clark|floyd|harrison|jackson|scott) county\b/i,
  // Louisville neighborhoods + nearby suburbs/communities
  /\b(highlands|crescent hill|clifton|cherokee triangle|germantown|portland|butchertown|nulu|old louisville|smoketown|shawnee|west end|east end|south end|fairdale|fern creek|okolona|valley station|pleasure ridge park|prp|middletown|anchorage|prospect|lyndon|graymoor|graymoor-devondale|hurstbourne|douglass hills|jeffersontown|j-town|fairmount|brownsboro|brownsboro village|indian hills|rolling hills|northfield|barbourmeade|glenview|riverwood|goshen|pewee valley|crestwood|buckner|el dorado|fisherville|eastwood|harrods creek|prospect hill|norton commons)\b/i,

  // Common spelling variants, punctuation differences, and local abbreviations.
  // These supplement the canonical names above so we match how real customers
  // actually type addresses (missing periods, extra hyphens, nicknames).
  // - "St"/"St."/"Saint" Matthews; "Mt"/"Mt."/"Mount" Washington
  // - "LaGrange" (no space) vs "La Grange"; "JTown"/"J Town" vs "J-Town"
  // - "Coxs Creek" (no apostrophe); "Ft Knox"/"Ft. Knox"/"Fort Knox"
  // - "PRP" already covered as a token; "P.R.P." spelled out below
  // - "NULU" / "Nu-Lu" stylings
  // - "E-town" / "Etown" for Elizabethtown
  /\b(saint matthews|st\.? matthews|mt\.? washington|mount washington|la[\s-]?grange|lagrange|j[\s.\-]?town|jtown|coxs creek|cox'?s creek|ft\.? knox|fort knox|p\.?r\.?p\.?|nu[\s-]?lu|e[\s-]?town|etown|fern[\s-]?creek|valley[\s-]?station|pleasure[\s-]?ridge[\s-]?park|new[\s-]?albany|jeff(ersonville)?|clarksville|sellersburg)\b/i,
  // Kentuckiana metro / regional shorthand
  /\b(louisville metro|metro louisville|greater louisville|derby city|river city|louisville ky|lou ky)\b/i,
  // Southern Indiana abbreviations + "SoIN" stylings
  /\b(so\.?\s*ind(iana)?|s\.?\s*indiana|southern ind|so[\s-]?in)\b/i,
  // States — written-out form OR state code adjacent to a comma/end of string
  /\b(kentucky|indiana|southern indiana|kentuckiana)\b/i,
  // Match KY/IN only when comma-prefixed (", KY") or followed by a zip code
  // ("KY 40202"), or at end-of-string. Prevents "in" from auto-whitelisting.
  /(?:,\s*(?:KY|IN)\b|\b(?:KY|IN)\s+\d{5}(?:-\d{4})?\b|\b(?:KY|IN)\s*$)/,

  // Property / project context
  /\b(commercial|industrial|residential|warehouse|office|facility|plant|factory|church|school|gym|arena|field)\b/i,
];

export type SpamReason = "honeypot" | "too_fast" | "keyword_match" | null;

export type SpamCheckResult = {
  isSpam: boolean;
  reason: SpamReason;
  /** The pattern that matched (only set when reason === 'keyword_match'). */
  matchedPattern?: string | undefined;
  /** When the submission passed via the whitelist, the rule that matched. */
  matchedWhitelist?: string | undefined;
  /** True if the submission passed via the per-browser learned allowlist. */
  matchedLearned?: boolean | undefined;
  /** ms elapsed since form mount at the time of the check. */
  elapsedMs: number;
  /** Form source identifier (e.g. 'contact_form', 'chat_widget'). Useful
   *  for segmenting blocked vs submitted leads in analytics later. */
  source?: string | undefined;
  /** Friendly label of the spelling variant detected in the raw submitted
   *  text (e.g. "NuLu", "J-Town", "Ft. Knox"). DEV-panel use only. */
  matchedVariantLabel?: string | undefined;
  /** Exact substring the user typed that triggered the variant detection. */
  matchedVariantText?: string | undefined;
};

export type SpamCheckInput = Record<string, unknown> & {
  /** Optional form-source tag. Excluded from keyword scanning. */
  source?: string | undefined;
};

export const IS_SPAM_DEBUG = import.meta.env.DEV;

/** GA4 segmentation event for blocked submissions. Fires alongside
 *  successful leads (which fire `generate_lead`). Console logging is now
 *  handled by recordSpamEvent() in spamDebugLog.ts (DEV only). */
function reportBlockedToGA(r: SpamCheckResult) {
  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag === "function") {
    w.gtag("event", "spam_blocked", {
      event_category: "form",
      event_label: r.source ?? "unknown",
      reason: r.reason,
      elapsed_ms: r.elapsedMs,
    });
  }
}

/** Single exit point for every checkSpam() outcome — keeps logging,
 *  GA reporting, and lastResult state in sync. */
function finalize(r: SpamCheckResult, set: (r: SpamCheckResult) => void): SpamCheckResult {
  set(r);
  recordSpamEvent(r);
  if (r.isSpam) reportBlockedToGA(r);
  return r;
}

export function useSpamProtection() {
  const mountTime = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const [lastResult, setLastResult] = useState<SpamCheckResult | null>(null);

  const honeypotProps = useMemo(
    () => ({
      ref: (el: HTMLInputElement | null) => {
        honeypotRef.current = el;
      },
      type: "text" as const,
      name: "hp_x7",
      id: "hp_x7",
      tabIndex: -1,
      autoComplete: "off",
      "aria-hidden": true,
      defaultValue: "",
      style: {
        position: "absolute" as const,
        left: "-9999px",
        opacity: 0,
        height: 0,
        width: 0,
        pointerEvents: "none" as const,
      },
    }),
    []
  );

  const checkSpam = (formData: SpamCheckInput): SpamCheckResult => {
    const elapsedMs = Date.now() - mountTime.current;
    const source = typeof formData.source === "string" ? formData.source : undefined;

    // 1. Honeypot
    const honeypotRaw = honeypotRef.current?.value ?? "";
    if (honeypotRaw.trim().length > 0) {
      return finalize({ isSpam: true, reason: "honeypot", elapsedMs, source }, setLastResult);
    }

    // 2. Time-trap
    if (elapsedMs < MIN_FILL_MS) {
      return finalize({ isSpam: true, reason: "too_fast", elapsedMs, source }, setLastResult);
    }

    // 3. Keyword blocklist — concatenate all free-text values and scan.
    //    `source` is excluded so a source like "quote_request" can never
    //    self-trigger a blocklist hit. Static whitelist OR learned allowlist
    //    (per-browser, 24h TTL) short-circuits the blocklist.
    const text = Object.entries(formData)
      .filter(([k, v]) => k !== "source" && typeof v === "string")
      .map(([, v]) => v as string)
      .join("\n");

    // Detect a known spelling-variant abbreviation in the RAW text (before
    // normalization) so the dev panel can show what the user actually typed.
    // Attached to every outcome from this point on — block or pass.
    const variant = detectVariantLabel(text);

    // Apply admin-only runtime overrides: per-browser disabled built-ins +
    // admin-added extras. Falls back to the built-in list with no overrides.
    const overrides = readOverrides();
    const disabledSet = new Set(overrides.disabled);
    const effectiveWhitelist: RegExp[] = [
      ...WHITELIST_PATTERNS.filter((_, i) => !disabledSet.has(i)),
      ...compileExtras(overrides.extras),
    ];

    // Whitelist matching runs against a NORMALIZED copy of the text so
    // common address variants ("St. Matthews", "St Matthews", "Saint
    // Matthews") all canonicalize to the same form before regex tests.
    // The raw `text` is still used for the spam blocklist below — we don't
    // want normalization to mask spam tells like odd punctuation/casing.
    const normalizedText = text.trim().length > 0 ? normalizeAddressText(text) : text;

    let matchedWhitelist: RegExp | undefined;
    if (text.trim().length > 0) {
      matchedWhitelist = effectiveWhitelist.find((p) => p.test(normalizedText));
    }
    const whitelisted = !!matchedWhitelist;
    const learned = !whitelisted && text.trim().length > 0 && textHasLearnedToken(text);
    if (text.trim().length > 0 && !whitelisted && !learned) {
      for (const pattern of SPAM_PATTERNS) {
        if (pattern.test(text)) {
          return finalize(
            {
              isSpam: true,
              reason: "keyword_match",
              matchedPattern: pattern.toString(),
              elapsedMs,
              source,
              matchedVariantLabel: variant?.label,
              matchedVariantText: variant?.matched,
            },
            setLastResult
          );
        }
      }
    }

    // Confidently-passing submission → harvest tokens for the 24h allowlist.
    // Only learns when the static whitelist matched + elapsed > 8s + msg ≥ 40
    // chars. See spamLearnedAllowlist.ts for the full guardrail set.
    if (whitelisted) {
      const longest = Object.entries(formData)
        .filter(([k, v]) => k !== "source" && typeof v === "string")
        .map(([, v]) => v as string)
        .reduce((a, b) => (b.length > a.length ? b : a), "");
      learnFromSubmission({ elapsedMs, whitelisted, message: longest });
    }

    return finalize(
      {
        isSpam: false,
        reason: null,
        elapsedMs,
        source,
        matchedWhitelist: matchedWhitelist?.toString(),
        matchedLearned: learned || undefined,
        matchedVariantLabel: variant?.label,
        matchedVariantText: variant?.matched,
      },
      setLastResult
    );
  };

  return { honeypotProps, mountTime, checkSpam, lastResult, debug: IS_SPAM_DEBUG };
}
